import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { UserPlus, Download, CheckCircle2, AlertTriangle, Award, FileSpreadsheet } from "lucide-react";
import BulkEnrollmentUpload from "@/components/BulkEnrollmentUpload";

/**
 * Admin panel: manually create a completed enrollment (backfill historical
 * certificates). Captures student name/email, selects a course, date and score,
 * calls POST /api/admin/manual-enrollment and offers the certificate download.
 */
export default function ManualCertificateAdmin() {
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({
    student_name: "",
    student_curp: "",
    student_email: "",
    course_id: "",
    completed_at: new Date().toISOString().slice(0, 10),
    final_exam_score: 100,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);   // last successful enrollment payload

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data || [])).catch(() => {});
  }, []);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setResult(null); setBusy(true);
    try {
      const curp = form.student_curp.trim().toUpperCase();
      const payload = {
        student_name: form.student_name.trim(),
        student_curp: curp,
        student_email: form.student_email.trim().toLowerCase() || null,
        course_id: form.course_id,
        completed_at: form.completed_at,
        final_exam_score: Number(form.final_exam_score),
      };
      if (!payload.student_name) throw new Error("El nombre del estudiante es obligatorio");
      if (!payload.student_curp) throw new Error("La CURP es obligatoria");
      if (!/^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/.test(payload.student_curp)) {
        throw new Error("CURP inválido — debe tener 18 caracteres siguiendo el formato oficial");
      }
      if (!payload.course_id) throw new Error("Selecciona un curso");
      const r = await api.post("/admin/manual-enrollment", payload);
      setResult(r.data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!result?.enrollment_id) return;
    try {
      const resp = await api.get(`/admin/certificates/${result.enrollment_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificado-${(result.student?.name || "estudiante").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const resetForm = () => {
    setForm({
      student_name: "",
      student_curp: "",
      student_email: "",
      course_id: "",
      completed_at: new Date().toISOString().slice(0, 10),
      final_exam_score: 100,
    });
    setResult(null);
    setErr("");
  };

  return (
    <div data-testid="manual-certificate-panel" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-naranja" />
            <h2 className="font-heading font-bold text-marino text-xl">Registro manual de certificado</h2>
          </div>
          <p className="text-sm text-marino/70 max-w-3xl mt-1">
            Usa esta sección para dar de alta un estudiante y su certificado de forma retroactiva.
            La <b>CURP</b> es el identificador único: si el alumno ya existe se reutilizará su perfil;
            si no tiene correo real se genera uno interno <code>CURP@om-pec.com</code> que podrá reclamar después.
          </p>
        </div>
        <a
          data-testid="link-bulk-upload"
          href="#bulk"
          onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="bulk-upload-panel"]')?.scrollIntoView({ behavior: "smooth" }); }}
          className="inline-flex items-center gap-2 border border-marino/20 text-marino px-3 py-2 rounded-lg text-sm hover:bg-marino/5"
        >
          <FileSpreadsheet className="w-4 h-4" /> Carga masiva CSV
        </a>
      </div>

      {err && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg max-w-3xl">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      <form onSubmit={submit} className="bg-white border border-gris/30 rounded-xl p-6 shadow-sm max-w-3xl space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-marino/70 mb-1">Nombre completo del estudiante</label>
            <input
              data-testid="manual-student-name"
              value={form.student_name}
              onChange={(e) => upd("student_name", e.target.value)}
              placeholder="Ej. Juan Pérez López"
              className="w-full border border-gris rounded-lg px-3 py-2 text-sm focus:border-marino focus:outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-marino/70 mb-1">CURP <span className="text-red-500">*</span></label>
            <input
              data-testid="manual-student-curp"
              value={form.student_curp}
              onChange={(e) => upd("student_curp", e.target.value.toUpperCase())}
              placeholder="PELJ900101HDFRPS08"
              maxLength={18}
              className="w-full border border-gris rounded-lg px-3 py-2 text-sm focus:border-marino focus:outline-none font-mono tracking-wider uppercase"
              required
            />
            <p className="text-[11px] text-marino/50 mt-1">18 caracteres. Identificador único del alumno para el certificado DC-3.</p>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-marino/70 mb-1">Email real <span className="text-marino/40">(opcional)</span></label>
          <input
            data-testid="manual-student-email"
            type="email"
            value={form.student_email}
            onChange={(e) => upd("student_email", e.target.value)}
            placeholder="Si el alumno no tiene correo, se generará CURP@om-pec.com automáticamente"
            className="w-full border border-gris rounded-lg px-3 py-2 text-sm focus:border-marino focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-marino/70 mb-1">Curso tomado</label>
          <select
            data-testid="manual-course-select"
            value={form.course_id}
            onChange={(e) => upd("course_id", e.target.value)}
            className="w-full border border-gris rounded-lg px-3 py-2 text-sm focus:border-marino focus:outline-none bg-white"
            required
          >
            <option value="">— Selecciona un curso —</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-marino/70 mb-1">Fecha de conclusión</label>
            <input
              data-testid="manual-completed-at"
              type="date"
              value={form.completed_at}
              onChange={(e) => upd("completed_at", e.target.value)}
              className="w-full border border-gris rounded-lg px-3 py-2 text-sm focus:border-marino focus:outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-marino/70 mb-1">Calificación final (%)</label>
            <input
              data-testid="manual-score"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={form.final_exam_score}
              onChange={(e) => upd("final_exam_score", e.target.value)}
              className="w-full border border-gris rounded-lg px-3 py-2 text-sm focus:border-marino focus:outline-none"
              required
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            data-testid="manual-submit-btn"
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 bg-naranja text-white px-4 py-2 rounded-lg font-medium hover:bg-naranjaHover disabled:opacity-60"
          >
            <Award className="w-4 h-4" /> {busy ? "Generando…" : "Generar certificado"}
          </button>
          <button
            type="button"
            onClick={resetForm}
            className="border border-gris text-marino px-4 py-2 rounded-lg font-medium hover:bg-marino/5"
          >
            Limpiar
          </button>
        </div>
      </form>

      {result && (
        <div data-testid="manual-result" className="bg-green-50 border border-green-200 rounded-xl p-5 max-w-3xl">
          <div className="flex items-center gap-2 text-green-800 font-heading font-bold">
            <CheckCircle2 className="w-5 h-5" /> Certificado listo
          </div>
          <div className="mt-2 text-sm text-marino grid sm:grid-cols-2 gap-x-6 gap-y-1">
            <div><span className="text-marino/60">Estudiante:</span> <b>{result.student?.name}</b></div>
            <div><span className="text-marino/60">Email:</span> {result.student?.email}</div>
            <div><span className="text-marino/60">Curso:</span> {result.course_title}</div>
            <div><span className="text-marino/60">Calificación:</span> {result.final_exam_score}%</div>
            <div><span className="text-marino/60">Fecha:</span> {result.completed_at?.slice(0, 10)}</div>
            <div><span className="text-marino/60">Enrollment:</span> <code className="text-xs">{result.enrollment_id}</code></div>
          </div>
          <div className="mt-3 text-xs text-marino/70 bg-white border border-green-200 rounded px-3 py-2 inline-flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            El certificado se está respaldando automáticamente en OneDrive.
          </div>
          <button
            data-testid="manual-download-btn"
            onClick={downloadPdf}
            className="mt-4 inline-flex items-center gap-2 bg-marino text-white px-4 py-2 rounded-lg font-medium hover:bg-marino/90"
          >
            <Download className="w-4 h-4" /> Descargar PDF
          </button>
        </div>
      )}

      <BulkEnrollmentUpload endpoint="/admin/bulk-enrollments" courses={courses} />
    </div>
  );
}
